import React, {useEffect, useState, useRef, useCallback, useLayoutEffect} from 'react'
import {createPortal} from 'react-dom'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {
    Box,
    Center,
    Spinner,
    Stack,
    Link,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {categoryUrlBuilder} from '../../utills/url'
import {parseCategoryColorConfig} from '../../utills/parseCategoryColorConfig'
import {MEGA_MENU_MAXIMUM_NUMBER_COLUMNS, MEGA_MENU_CONTENT_SLOT_ID} from '../../constants'
import {useHeaderNavLayout} from '../header-v2/header-nav-layout-context'
import {pxToRem} from '../../utills/pxToRem'
import {usePreview} from '../../contexts/StorefrontPreviewContext'
import {prefetchSlotContent} from '../../hooks/useFetchContent'

const HOVER_DELAY_MS = 500
const HOVER_CLOSE_DELAY_MS = 500

const MegaMenu = ({
    root,
    contentComponent: ContentComponent,
    itemsKey = 'categories',
    itemsCountKey = 'onlineSubCategoriesCount',
    maxColumns = MEGA_MENU_MAXIMUM_NUMBER_COLUMNS
}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.MegaMenu
    const {isTwoRow = false} = useHeaderNavLayout()
    const intl = useIntl()
    const location = useLocation()
    const {app: appConfig} = getConfig()
    const {previewData = {}} = usePreview()

    const stackContainerProps = {
        ...baseStyle.stackContainer,
        ...(isTwoRow && {
            gap: pxToRem(10),
            justifyContent: 'flex-start',
            flex: '0 0 auto'
        })
    }

    const triggerRefs = useRef({})
    const dropdownRef = useRef(null)

    const tabletFontSize = pxToRem(11)
    const getTriggerLinkExtraStyle = (label) => {
        const hasMultipleWords = typeof label === 'string' && label.trim().includes(' ')
        const wrapStyle = {
            whiteSpace: 'normal',
            minWidth: 0,
            overflow: 'hidden',
            textAlign: 'center',
            lineHeight: 1.2
        }
        const tabletBase = {
            fontSize: tabletFontSize,
            paddingLeft: pxToRem(6),
            paddingRight: pxToRem(6),
            ...wrapStyle
        }
        if (hasMultipleWords) {
            return isTwoRow ? tabletBase : wrapStyle
        }
        if (isTwoRow) {
            return {
                fontSize: tabletFontSize,
                paddingLeft: pxToRem(6),
                paddingRight: pxToRem(6),
                textAlign: 'center',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                minWidth: 'min-content'
            }
        }
        return {}
    }

    const getTriggerContainerExtraStyle = (label) => {
        if (!isTwoRow) return {}
        const hasMultipleWords = typeof label === 'string' && label.trim().includes(' ')
        if (hasMultipleWords) return {}
        return {flex: '0 1 auto', minWidth: 'min-content'}
    }

    const [ariaBusy, setAriaBusy] = useState(true)
    const [activeCategory, setActiveCategory] = useState(null)
    const [activeCategoryItem, setActiveCategoryItem] = useState(null)
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState(null)

    const hoverOpenTimerRef = useRef(null)
    const hoverCloseTimerRef = useRef(null)
    const navRef = useRef(null)
    const [dropdownSlot, setDropdownSlot] = useState(null)

    const items = root?.[itemsKey]

    useLayoutEffect(() => {
        if (!activeCategory) {
            setDropdownSlot(null)
            return
        }
        const slot = document.getElementById('mega-menu-dropdown-slot')
        setDropdownSlot(slot || null)
    }, [activeCategory])

    // Dynamically compute max height based on available viewport space
    useLayoutEffect(() => {
        if (!activeCategory || !dropdownRef.current) {
            setDropdownMaxHeight(null)
            return
        }
        const computeMaxHeight = () => {
            if (!dropdownRef.current) return
            const rect = dropdownRef.current.getBoundingClientRect()
            const available = window.innerHeight - rect.top - 50
            setDropdownMaxHeight(Math.max(available, 200))
        }
        requestAnimationFrame(computeMaxHeight)
        window.addEventListener('resize', computeMaxHeight)
        return () => window.removeEventListener('resize', computeMaxHeight)
    }, [activeCategory, dropdownSlot])

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    useEffect(() => {
        handleClose()
    }, [location])

    useEffect(() => {
        return () => {
            clearTimeout(hoverOpenTimerRef.current)
            clearTimeout(hoverCloseTimerRef.current)
        }
    }, [])

    useEffect(() => {
        if (!items?.length) return
        items.forEach((item) => {
            const slotId = `${MEGA_MENU_CONTENT_SLOT_ID}${encodeURIComponent(item.id)}`
            prefetchSlotContent(`c_contentSlotIDs=${slotId}`, appConfig, previewData)
        })
    }, [items])

    const handleClose = useCallback((returnFocusToId = null) => {
        clearTimeout(hoverOpenTimerRef.current)
        clearTimeout(hoverCloseTimerRef.current)
        setActiveCategory(null)
        setActiveCategoryItem(null)

        if (returnFocusToId && triggerRefs.current[returnFocusToId]) {
            triggerRefs.current[returnFocusToId].focus()
        }
    }, [])

    // Close mega menu on click outside or Escape key
    useEffect(() => {
        if (!activeCategory) return

        const handleClickOutside = (e) => {
            if (
                navRef.current &&
                !navRef.current.contains(e.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                handleClose()
            }
        }

        const handleEscKey = (e) => {
            if (e.key === 'Escape') {
                handleClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscKey)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscKey)
        }
    }, [activeCategory, handleClose])

    const handleTriggerMouseEnter = useCallback(
        (item) => {
            clearTimeout(hoverCloseTimerRef.current)
            clearTimeout(hoverOpenTimerRef.current)
            if (activeCategory !== null) {
                setActiveCategory(item.id)
                setActiveCategoryItem(item)
                return
            }
            hoverOpenTimerRef.current = setTimeout(() => {
                setActiveCategory(item.id)
                setActiveCategoryItem(item)
            }, HOVER_DELAY_MS)
        },
        [activeCategory]
    )

    const handleTriggerMouseLeave = useCallback(() => {
        clearTimeout(hoverOpenTimerRef.current)
        hoverCloseTimerRef.current = setTimeout(() => {
            setActiveCategory(null)
            setActiveCategoryItem(null)
        }, HOVER_CLOSE_DELAY_MS)
    }, [])

    const handleDropdownMouseEnter = useCallback(() => {
        clearTimeout(hoverCloseTimerRef.current)
        clearTimeout(hoverOpenTimerRef.current)
    }, [])

    const handleDropdownMouseLeave = useCallback(() => {
        hoverCloseTimerRef.current = setTimeout(() => {
            setActiveCategory(null)
            setActiveCategoryItem(null)
        }, HOVER_CLOSE_DELAY_MS)
    }, [])

    const handleTriggerKeyDown = useCallback(
        (event, item) => {
            const {key, shiftKey} = event
            const hasSubMenu = (item[itemsCountKey] || item[itemsKey]?.length || 0) > 0

            if (!hasSubMenu) return

            // Space or Enter key opens the megamenu and focuses first item
            if (key === ' ' || key === 'Enter') {
                event.preventDefault()
                clearTimeout(hoverCloseTimerRef.current)
                clearTimeout(hoverOpenTimerRef.current)
                setActiveCategory(item.id)
                setActiveCategoryItem(item)

                setTimeout(() => {
                    dropdownRef.current?.querySelector('a[href]:not([disabled])')?.focus()
                }, 100)
                return
            }

            // ArrowDown opens the megamenu and focuses first item
            if (key === 'ArrowDown') {
                event.preventDefault()
                clearTimeout(hoverCloseTimerRef.current)
                clearTimeout(hoverOpenTimerRef.current)
                setActiveCategory(item.id)
                setActiveCategoryItem(item)

                setTimeout(() => {
                    dropdownRef.current?.querySelector('a[href]:not([disabled])')?.focus()
                }, 100)
                return
            }

            // Escape closes the megamenu
            if (key === 'Escape') {
                event.preventDefault()
                handleClose(item.id)
            }
        },
        [itemsKey, itemsCountKey, handleClose]
    )

    const handleDropdownKeyDown = useCallback(
        (event) => {
            const {key, shiftKey} = event

            // Escape closes the megamenu and returns focus to trigger
            if (key === 'Escape') {
                event.preventDefault()
                handleClose(activeCategoryItem?.id)
                return
            }

            // Tab key closes dropdown and moves to next L1 item
            if (key === 'Tab') {
                event.preventDefault()
                const items = root?.[itemsKey] || []
                const currentIndex = items.findIndex((item) => item.id === activeCategoryItem?.id)

                if (shiftKey) {
                    // Shift+Tab: move to previous L1 item or close if at first
                    if (currentIndex > 0) {
                        const prevItem = items[currentIndex - 1]
                        handleClose()
                        setTimeout(() => {
                            triggerRefs.current[prevItem.id]?.focus()
                        }, 50)
                    } else {
                        handleClose(activeCategoryItem?.id)
                    }
                } else {
                    // Tab: move to next L1 item or close if at last
                    if (currentIndex < items.length - 1) {
                        const nextItem = items[currentIndex + 1]
                        handleClose()
                        setTimeout(() => {
                            triggerRefs.current[nextItem.id]?.focus()
                        }, 50)
                    } else {
                        handleClose()
                    }
                }
                return
            }

            // Arrow keys navigate within the dropdown
            if (
                key === 'ArrowDown' ||
                key === 'ArrowUp' ||
                key === 'ArrowLeft' ||
                key === 'ArrowRight'
            ) {
                event.preventDefault()
                const focusableElements =
                    dropdownRef.current?.querySelectorAll('a[href]:not([disabled])')
                if (!focusableElements || focusableElements.length === 0) return

                const currentIndex = Array.from(focusableElements).indexOf(document.activeElement)

                let nextIndex = currentIndex

                if (key === 'ArrowDown') {
                    nextIndex =
                        currentIndex === -1 || currentIndex === focusableElements.length - 1
                            ? 0
                            : currentIndex + 1
                } else if (key === 'ArrowUp') {
                    nextIndex =
                        currentIndex === -1 || currentIndex === 0
                            ? focusableElements.length - 1
                            : currentIndex - 1
                } else if (key === 'ArrowRight') {
                    const currentElement =
                        currentIndex >= 0 ? focusableElements[currentIndex] : null
                    const tolerance = 5

                    if (currentElement) {
                        const currentRect = currentElement.getBoundingClientRect()
                        const currentY = currentRect.top

                        let found = false
                        for (let i = currentIndex + 1; i < focusableElements.length; i++) {
                            const rect = focusableElements[i].getBoundingClientRect()
                            if (
                                rect.left > currentRect.left &&
                                Math.abs(rect.top - currentY) < tolerance
                            ) {
                                nextIndex = i
                                found = true
                                break
                            }
                        }

                        if (!found) {
                            nextIndex =
                                currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0
                        }
                    } else {
                        nextIndex = 0
                    }
                } else if (key === 'ArrowLeft') {
                    const currentElement =
                        currentIndex >= 0 ? focusableElements[currentIndex] : null
                    const tolerance = 5

                    if (currentElement) {
                        const currentRect = currentElement.getBoundingClientRect()
                        const currentY = currentRect.top

                        let found = false
                        for (let i = currentIndex - 1; i >= 0; i--) {
                            const rect = focusableElements[i].getBoundingClientRect()
                            if (
                                rect.left < currentRect.left &&
                                Math.abs(rect.top - currentY) < tolerance
                            ) {
                                nextIndex = i
                                found = true
                                break
                            }
                        }

                        if (!found) {
                            nextIndex =
                                currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1
                        }
                    } else {
                        nextIndex = focusableElements.length - 1
                    }
                }

                focusableElements[nextIndex]?.focus()
            }
        },
        [activeCategoryItem, handleClose, root, itemsKey]
    )

    return (
        <nav
            ref={navRef}
            id="mega-menu"
            aria-label={intl.formatMessage({
                id: 'mega_menu.nav.assistive_msg',
                defaultMessage: 'Main navigation'
            })}
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
            style={{
                flex: 1,
                minHeight: 0,
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                position: 'relative',
                paddingTop: pxToRem(1),
                ...(isTwoRow && {alignItems: 'flex-start'})
            }}
        >
            {items ? (
                <>
                    <Stack
                        direction="row"
                        spacing={0}
                        flex="0 0 auto"
                        width="100%"
                        minWidth={0}
                        {...stackContainerProps}
                    >
                        {items.map((item) => {
                            const {id, name, c_categoryTextColor, c_categoryIcon} = item
                            const itemsCount = item[itemsCountKey] || item[itemsKey]?.length || 0
                            const hasSubMenu = itemsCount > 0
                            const isActive = activeCategory === id

                            const parsedColors = parseCategoryColorConfig(c_categoryTextColor)
                            const triggerColor = parsedColors?.textColor
                            const triggerHoverColor = parsedColors?.hoverColor

                            return (
                                <Box
                                    key={id}
                                    {...baseStyle.listMenuTriggerContainer}
                                    {...getTriggerContainerExtraStyle(name)}
                                    {...(isActive ? baseStyle.listMenuTriggerContainerActive : {})}
                                >
                                    <Link
                                        ref={(el) => {
                                            if (el) triggerRefs.current[id] = el
                                        }}
                                        href={categoryUrlBuilder(item)}
                                        onMouseEnter={
                                            hasSubMenu
                                                ? () => handleTriggerMouseEnter(item)
                                                : undefined
                                        }
                                        onMouseLeave={
                                            hasSubMenu ? handleTriggerMouseLeave : undefined
                                        }
                                        onKeyDown={(e) => handleTriggerKeyDown(e, item)}
                                        aria-haspopup={hasSubMenu ? 'true' : undefined}
                                        aria-expanded={
                                            hasSubMenu ? (isActive ? 'true' : 'false') : undefined
                                        }
                                        aria-controls={
                                            hasSubMenu ? `mega-menu-dropdown-${id}` : undefined
                                        }
                                        {...baseStyle.listMenuTriggerLink}
                                        {...getTriggerLinkExtraStyle(name)}
                                        {...(isActive ? baseStyle.listMenuTriggerLinkActive : {})}
                                        {...(triggerColor ? {color: triggerColor} : {})}
                                        {...(triggerHoverColor
                                            ? {
                                                  _hover: {
                                                      ...baseStyle.listMenuTriggerLink._hover,
                                                      color: triggerHoverColor
                                                  },
                                                  _focus: {
                                                      ...baseStyle.listMenuTriggerLink._focus,
                                                      color: triggerHoverColor
                                                  },
                                                  _focusVisible: {
                                                      ...baseStyle.listMenuTriggerLink
                                                          ._focusVisible,
                                                      color: triggerHoverColor
                                                  }
                                              }
                                            : {})}
                                        data-testid={`mega-menu-trigger-${id}`}
                                    >
                                        {c_categoryIcon && (
                                            <Box
                                                as="span"
                                                marginRight={pxToRem(4)}
                                                display="inline-flex"
                                                alignItems="center"
                                                dangerouslySetInnerHTML={{
                                                    __html: c_categoryIcon
                                                }}
                                            />
                                        )}
                                        {name}
                                    </Link>
                                </Box>
                            )
                        })}
                    </Stack>

                    {activeCategory &&
                        activeCategoryItem &&
                        dropdownSlot &&
                        createPortal(
                            <Box
                                ref={dropdownRef}
                                id={`mega-menu-dropdown-${activeCategoryItem.id}`}
                                role="region"
                                aria-label={intl.formatMessage(
                                    {
                                        id: 'mega_menu.dropdown.assistive_msg',
                                        defaultMessage: '{categoryName} submenu'
                                    },
                                    {categoryName: activeCategoryItem.name}
                                )}
                                aria-live="polite"
                                width="100%"
                                backgroundColor="white"
                                boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                                onMouseEnter={handleDropdownMouseEnter}
                                onMouseLeave={handleDropdownMouseLeave}
                                onKeyDown={handleDropdownKeyDown}
                                data-testid={`mega-menu-dropdown-${activeCategoryItem?.id}`}
                                sx={{
                                    boxSizing: 'border-box',
                                    '&::-webkit-scrollbar': {display: 'none'},
                                    msOverflowStyle: 'none',
                                    scrollbarWidth: 'none'
                                }}
                                minHeight={pxToRem(400)}
                                maxHeight={dropdownMaxHeight ? `${dropdownMaxHeight}px` : undefined}
                                overflowY={dropdownMaxHeight ? 'auto' : undefined}
                                display="flex"
                                flexDirection="column"
                            >
                                <Box {...baseStyle.popoverBody}>
                                    <ContentComponent
                                        item={activeCategoryItem}
                                        itemsKey={itemsKey}
                                        onClose={handleClose}
                                        maxColumns={maxColumns}
                                    />
                                </Box>
                            </Box>,
                            dropdownSlot
                        )}
                </>
            ) : (
                <Center p="2">
                    <Spinner size="lg" />
                </Center>
            )}
        </nav>
    )
}

MegaMenu.displayName = 'MegaMenu'

MegaMenu.propTypes = {
    root: PropTypes.object,
    maxColumns: PropTypes.number,
    itemsKey: PropTypes.string,
    itemsCountKey: PropTypes.string,
    contentComponent: PropTypes.elementType
}

export {MegaMenu}
export default MegaMenu
